import { useState } from "react";
import { History, Plus, MessageSquare, Trash2, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { formatTimeAgo } from "@/components/agent/display/utils/formatters";
import type { BrainChatSession } from "@/types/brain/brain";

interface SessionHistoryPopoverProps {
  sessions: BrainChatSession[];
  activeSessionId: string | null;
  onSelectSession: (sessionId: string | null) => void;
  onNewChat: () => void;
  onDeleteSession: (sessionId: string) => Promise<void>;
  isLoading?: boolean;
}

const SessionHistoryPopover = ({
  sessions,
  activeSessionId,
  onSelectSession,
  onNewChat,
  onDeleteSession,
  isLoading,
}: SessionHistoryPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const handleSelect = (sessionId: string) => {
    onSelectSession(sessionId);
    setOpen(false);
  };

  const handleDelete = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    setDeletingId(sessionId);
    try {
      await onDeleteSession(sessionId);
      if (sessionId === activeSessionId) {
        onSelectSession(null);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const toggleSelectMode = () => {
    setSelectMode((prev) => !prev);
    setSelectedIds(new Set());
  };

  const toggleSessionSelected = (sessionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const idsToDelete = Array.from(selectedIds);
    if (idsToDelete.length === 0) return;
    setIsBulkDeleting(true);
    try {
      await Promise.allSettled(idsToDelete.map((id) => onDeleteSession(id)));
      if (activeSessionId && selectedIds.has(activeSessionId)) {
        onSelectSession(null);
      }
      setSelectedIds(new Set());
      setSelectMode(false);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleNewChat = () => {
    onNewChat();
    setOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSelectMode(false);
      setSelectedIds(new Set());
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500">
          <History className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 max-w-[calc(100vw-2rem)] p-0"
        align="end"
        sideOffset={8}
      >
        <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700">Chat History</h4>
          {sessions.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-gray-500"
              onClick={toggleSelectMode}
            >
              {selectMode ? (
                <>
                  <X className="h-3 w-3 mr-1" />
                  Cancel
                </>
              ) : (
                "Select"
              )}
            </Button>
          )}
        </div>

        <ScrollArea className="max-h-[60vh]">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-gray-400">Loading...</div>
          ) : sessions.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-400">No conversations yet</div>
          ) : (
            <div className="py-1">
              {sessions.map((session) => (
                <div
                  key={session.session_id}
                  className={cn(
                    "group w-full flex items-start gap-2.5 px-3 py-2.5 hover:bg-gray-50 transition-colors min-h-[44px]",
                    session.session_id === activeSessionId && "bg-primary/5",
                  )}
                >
                  {selectMode && (
                    <Checkbox
                      checked={selectedIds.has(session.session_id)}
                      onCheckedChange={() => toggleSessionSelected(session.session_id)}
                      className="flex-shrink-0 mt-1"
                    />
                  )}
                  <button
                    onClick={() =>
                      selectMode
                        ? toggleSessionSelected(session.session_id)
                        : handleSelect(session.session_id)
                    }
                    className="flex items-start gap-2.5 flex-1 min-w-0 text-left"
                  >
                    <MessageSquare className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm text-gray-700 line-clamp-2 break-words"
                        title={session.preview || "New conversation"}
                      >
                        {session.preview || "New conversation"}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-gray-400">
                          {formatTimeAgo(session.last_message_at)}
                        </span>
                        <span className="text-[11px] text-gray-300">
                          {session.message_count} msg{session.message_count !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  </button>
                  {!selectMode && (
                    <button
                      onClick={(e) => handleDelete(e, session.session_id)}
                      disabled={deletingId === session.session_id}
                      className={cn(
                        "flex-shrink-0 mt-0.5 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-60",
                        deletingId === session.session_id
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100",
                      )}
                    >
                      {deletingId === session.session_id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="p-2 border-t border-gray-100">
          {selectMode ? (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center text-sm gap-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
              onClick={handleBulkDelete}
              disabled={selectedIds.size === 0 || isBulkDeleting}
            >
              {isBulkDeleting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              Delete{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center text-sm gap-1.5"
              onClick={handleNewChat}
            >
              <Plus className="h-3.5 w-3.5" />
              New Chat
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SessionHistoryPopover;
